// Unique number -> "Location/Room/Device", for ANY device on the broker.
//
// WHY THIS EXISTS
// The console watches '#', so it sees traffic from every device Smartify has
// deployed — not just the ones belonging to the signed-in user. Naming them from
// `getDevicesByUser` therefore labels a handful and calls the rest "Unknown
// device", which is exactly backwards for a diagnostic tool: the numbers you
// most need identified are the ones that aren't yours.
//
// `GetDeviceDetailbyUniqueNumber` answers for any unique number regardless of
// ownership, so it is the authority here. The user's own device list is still
// used, as a free prime — it is already loaded and saves a request per device.
//
// COST CONTROL. One request per unique number, ever:
//   • results are cached, INCLUDING misses (a number that resolves to nothing is
//     never asked about twice)
//   • lookups run one at a time through a queue with a gap between them, so a
//     burst of unfamiliar numbers in the log trickles rather than floods
//   • a hard ceiling of MAX_LOOKUPS per session, after which unknown numbers
//     simply stay unlabelled — a console that DoSes the API to draw captions has
//     its priorities wrong
//
// Callers read synchronously (`getLabel`) and subscribe for repaints; nothing
// renders behind an await.

import { getDeviceDetail } from '../api/api';

// Ceiling on server lookups per session. Generous enough to name everything on a
// normal broker, low enough to bound the damage if '#' is unexpectedly busy.
export const MAX_LOOKUPS = 300;

// Gap between queued lookups.
const STEP_MS = 150;

// How often subscribers are told about newly resolved labels. Coalesced for the
// same reason the message buffer coalesces: a resolved label repaints a 500-row
// list, and doing that per lookup makes the console stutter.
const FLUSH_MS = 250;

// uno -> label. An empty string means "asked, no such device" and is a real
// cached answer, not a miss — that is why lookups test `has`, not truthiness.
const cache = new Map();

const queue = [];
const queued = new Set();
let lookups = 0;
let running = false;

const listeners = new Set();
let flushTimer = null;

function notify() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    listeners.forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
  }, FLUSH_MS);
}

// The one place the caption's shape is decided.
//
//   Pankaj/Home/Ground Floor/WATER PUMP
//   owner /loc /room        /device
//
// The owner leads because this console sees the whole broker: "Home/Ground
// Floor" is ambiguous across customers and only the owner's name disentangles
// them. It comes from DeviceMasterUserName, which the detail endpoint returns
// and the user's own device list does not — so your own devices stay at the
// short three-part form, which is the right emphasis anyway.
//
// Joined with a bare "/" and no spaces: this rides on the end of every log line,
// where each character costs width the payload needs more.
export function labelOfDevice(d) {
  if (!d) return '';
  return [d.DeviceMasterUserName, d.LocationName, d.RoomName, d.DeviceName]
    .filter(Boolean).join('/');
}

const key = (uno) => String(uno ?? '').trim();

// Seed from a device list already in hand (the user's own devices). Free, and
// covers the common case without a single request.
export function primeLabels(devices) {
  let added = 0;
  (devices || []).forEach((d) => {
    const k = key(d.UniqueNumber);
    const text = labelOfDevice(d);
    // Only fill blanks: a list row is not more authoritative than a detail
    // fetch, and overwriting would make labels flicker between the two shapes.
    if (k && text && !cache.get(k)) { cache.set(k, text); added += 1; }
  });
  if (added) notify();
}

// What we know right now. null = never looked up; '' = looked up, not a device.
export function getLabel(uno) {
  const k = key(uno);
  return cache.has(k) ? cache.get(k) : null;
}

async function lookup(k) {
  try {
    const res = await getDeviceDetail(k);
    const rows = Array.isArray(res?.data) ? res.data : [];
    cache.set(k, labelOfDevice(rows[0]));
  } catch (e) {
    // A failed request is NOT a cached miss — the number may well be a device,
    // the network just said no. Leaving it uncached lets a later mention retry.
    return;
  }
  notify();
}

async function drain() {
  if (running) return;
  running = true;
  while (queue.length && lookups < MAX_LOOKUPS) {
    const k = queue.shift();
    queued.delete(k);
    if (!cache.has(k)) {
      lookups += 1;
      // eslint-disable-next-line no-await-in-loop
      await lookup(k);
      // eslint-disable-next-line no-await-in-loop
      if (queue.length) await new Promise((r) => setTimeout(r, STEP_MS));
    }
  }
  running = false;
}

// Ask for a label in the background. Safe to call for every log row on every
// render: already-known and already-queued numbers cost nothing.
export function requestLabel(uno) {
  const k = key(uno);
  if (!k || cache.has(k) || queued.has(k)) return;
  if (lookups >= MAX_LOOKUPS) return;
  queued.add(k);
  queue.push(k);
  drain();
}

// Resolve now, ahead of the queue — for the one number the user is actually
// looking at (the command box), which should not wait behind background work.
//
// Does not count against MAX_LOOKUPS: that ceiling exists to bound traffic the
// user did not ask for, and this is a direct response to something they typed.
export async function resolveLabel(uno) {
  const k = key(uno);
  if (!k) return '';
  if (cache.has(k)) return cache.get(k);
  await lookup(k);
  return cache.has(k) ? cache.get(k) : '';
}

export function subscribeLabels(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
